import axios from "axios";
import router from "./../router";
import store from "./../vuex";
import * as types from "./../vuex/modules/app/mutation-type.js";
import NProgress from "nprogress";
import { buildEncryptedRequest, decryptResponse } from './crypto.js';

// axios 配置
axios.defaults.timeout = 120000;
axios.defaults.baseURL = process.env.VUE_APP_API_ROOT;
axios.defaults.headers.post["Content-Type"] = "application/json";

// 默认初始化：未设置 API_DEBUG 时默认为加密模式（"0"），设 "1" 表示明文调试
if (!sessionStorage.getItem("API_DEBUG")) {
  sessionStorage.setItem("API_DEBUG", "0");
}

// 判断当前请求是否需要加密：仅生产构建且未开启调试模式时加密
function shouldEncrypt() {
  return process.env.VUE_APP_IS_BUILD === "1" && sessionStorage.getItem("API_DEBUG") !== "1";
}

// 对请求进行 AES + RSA 加密，跳过验证码接口 y/generatecode
function applyEncryption(config) {
  const encrypt = shouldEncrypt() && config.url !== 'y/generatecode';
  config.headers['X-Debug-Mode'] = encrypt ? "0" : "1";
  if (!encrypt) return config;

  try {
    const { requestConfig, aesKeyEncrypted, aesKey, iv } = buildEncryptedRequest({
      url: config.url,
      method: config.method,
      data: config.data,
      encryptUrlFields: config.encryptUrlFields || [],
    });
    config.url = requestConfig.url;
    config.data = requestConfig.data;
    config.headers['X-AES-Key'] = aesKeyEncrypted;
    config.headers['X-AES-IV'] = iv;
    config.meta = { ...config.meta, aesKey };
  } catch (error) {
    console.error('请求加密失败：', error);
    throw new Error(`请求加密失败：${error.message}`);
  }
  return config;
}

// http request 拦截器
axios.interceptors.request.use(
  config => {
    if (sessionStorage.token) {
      config.headers.token = `${sessionStorage.token}`;
    } else if (sessionStorage.mkYHTToken) {
      config.headers.token = `${sessionStorage.mkYHTToken}`;
    }
    const language = sessionStorage.getItem('X-Language') || 'zh-CN';
    config.headers['X-Language'] = language;

    // FormData 上传：删除 Content-Type 让浏览器自动设置 multipart/form-data + boundary，
    // 并跳过加密（FormData 无法 JSON.stringify，加密会破坏二进制数据）
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      delete config.headers.post?.['Content-Type'];
      delete config.headers['Content-Type'];
      config.headers['X-Debug-Mode'] = '1'; // 标记为明文模式，不加密
      config._isFormData = true; // 标记，供响应拦截器识别
      NProgress.start();
      return config;
    }

    // 请求加密（FormData 跳过，走明文）
    applyEncryption(config);
    NProgress.start();
    return config;
  },
  err => {
    return Promise.reject(err);
  }
);

// http response 拦截器
axios.interceptors.response.use(
  response => {
    NProgress.done();
    const { config, data } = response;

    // FormData 上传请求：响应不加密，直接返回完整 response（createUploader 期望 res.data）
    if (config._isFormData) {
      return response;
    }

    // 以「请求是否真的加密了」为准解密：请求加密时会暂存 config.meta.aesKey
    const aesKey = config.meta?.aesKey;
    if (!aesKey) {
      return response; // 请求未加密（明文调试 / FormData / generatecode），原样返回
    }
    try {
      response.data = decryptResponse(data, aesKey);
      return response;
    } catch (error) {
      console.error('响应解密失败：', error);
      throw new Error(`响应解密失败：${error.message}`);
    }
  },
  error => {
    NProgress.done();
    if (error.response?.config?.meta?.aesKey) {
      try {
        const decryptedError = decryptResponse(
          error.response.data,
          error.response.config.meta.aesKey
        );
        error.decryptedData = decryptedError;
      } catch (e) {
        console.error('错误响应解密失败：', e);
      }
    }
    
    if (error.status) {
      switch (error.status) {
        case 401:
          // 401 清除token信息并跳转到登录页面
          store.commit(types.LOGOUT);
          router.replace({
            path: "login",
            query: {
              redirect: router.currentRoute.fullPath
            }
          });
          break;
      }
    }
    return Promise.reject(error);

  }
);

let http = {
  get: function(url, params) {
    return new Promise((resolve, reject) => {
      axios
        .get(url, {
          params
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  post: function(url, data) {
    return new Promise((resolve, reject) => {
      axios
        .post(url, data)
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  put: function (url, data) {
    return axios.put(url, data).then(response => response.data)
  },
  delete: function (url, params) {
    return axios.delete(url, { params }).then(response => response.data)
  }
};

http.axios = axios;

export default http;
